import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "bank-id-app" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 1,
  class: "bank-id-app__loading-wrapper"
}
const _hoisted_4 = {
  key: 2,
  class: "bank-id-app__qr-code"
}
const _hoisted_5 = ["src"]
const _hoisted_6 = {
  key: 3,
  class: "form-error bank-id-app__error"
}
const _hoisted_7 = ["onClick"]

import {computed, onBeforeMount, Ref, ref} from 'vue';
import {useApi} from '@shared/services/api-service';
import {BankIdDevice} from "@features/BankIdApp/Scripts/constants/BankIdDeviceEnum";
import {BankIdAppState, BankIdState} from "@features/BankIdApp/Scripts/types/BankIdAppState";
import {useLoginBlockService} from "@features/BankIdApp/Scripts/services/login-block-service";
import {useBankIdService} from "@features/BankIdApp/Scripts/services/bank-id-service";
import Spinner from "@features/Scripts/shared/components/spinner/Spinner.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'BankIdApp',
  props: {
  sign: {
    type: Boolean,
    default: false,
  },
},
  emits: ["signed", "failed"],
  setup(__props, { emit: __emit }) {

const translations: Ref<BankIdAppTranslations | null> = ref(null);
const {get: getTranslations, data: translationsData, loading: translationsLoading} = useApi('bankidapp/gettranslations');
const qrCodeSrc = ref<string | null>(null);
const errorMessage = ref<string | null>(null); // possibly add an error state and return correct buttons for that
let pollHandle: number | null = null;
const emit = __emit;

const bankIdSignLoading = ref(false);

const sessionId = ref<string | null>(null);


onBeforeMount(() => {
  getTranslations().then(() => {
    translations.value = translationsData.value;
    if (translations.value) {
      translations.value.initialPreamble = useLoginBlockService().getTranslations()?.preamble;
    }
  });
});

const selectedDevice = ref<BankIdDevice | null>(null);
const state = computed<BankIdAppState>(() =>
    selectedDevice.value === null
        ? BankIdState.Initial
        : selectedDevice.value === BankIdDevice.ThisDevice
            ? BankIdState.ThisDevice
            : BankIdState.ExternalDevice);

const preamble = computed((): string | null => {
  if (bankIdSignLoading.value) {
    return null;
  }

  switch (state.value) {
    case BankIdState.Initial:
      return translations.value?.initialPreamble ?? null;
    case BankIdState.ThisDevice:
      return translations.value?.startBankIdHelp ?? null;
    case BankIdState.ExternalDevice:
      return translations.value?.startBankIdExternalHelp ?? null;
    default:
      return null;
  }
});

const buttons = computed<BankIdAppButton[]>(() => {
  switch (state.value) {
    case BankIdState.Initial:
      return [
        {
          text: translations.value?.thisDevice,
          action: onThisDevice,
        },
        {
          text: translations.value?.externalDevice,
          action: onOtherDevice,
        },
      ];
    case BankIdState.ThisDevice:
      return [
        {
          text: translations.value?.startBankId,
          action: tryOpenOnThisDevice,
        },
        {
          text: translations.value?.cancel,
          action: cancel,
          modifier: 'button--secondary'
        },
      ];
    case BankIdState.ExternalDevice:
      return [
        {
          text: translations.value?.cancel,
          action: cancel,
        },
      ];
    default:
      return [];
  }
});

const clearPolling = () => {
  if (pollHandle) {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
};

const onThisDevice = () => {
  selectedDevice.value = BankIdDevice.ThisDevice;
  startBankId();
};

const onOtherDevice = () => {
  selectedDevice.value = BankIdDevice.ExternalDevice;
  startBankId();
};

const tryOpenOnThisDevice = () => {
  onThisDevice();
};

const cancel = () => {
  useBankIdService().cancelBankId(sessionId.value).then(() => {
    selectedDevice.value = null;
    qrCodeSrc.value = null;
    errorMessage.value = null;
    clearPolling();

    if (__props.sign) {
      emit("failed", "Canceled");
    }
  });
};

const startBankId = () => {
  useBankIdService().startBankIdAuth(selectedDevice.value, isMobileDevice(), __props.sign).then((bankIdResponse) => {

    if (!bankIdResponse) {
      errorMessage.value = "Något gick fel";
      return;
    }

    if (bankIdResponse?.sessionId) {
      sessionId.value = bankIdResponse?.sessionId;
    }

    // qr code
    if (state.value === BankIdState.ExternalDevice) {
      qrCodeSrc.value = bankIdResponse?.qrCode ?? null;
    }

    // Set autostart app
    if (selectedDevice.value == BankIdDevice.ThisDevice && bankIdResponse?.autoStartUrl) {
      window.location.href = bankIdResponse?.autoStartUrl;
    }

    startPolling();
  }).catch((e: unknown) => {
    handleError(e);
  });
};

const startPolling = () => {
  pollHandle = window.setInterval(async () => {
    useBankIdService().statusBankId(sessionId.value ?? '').then((bankIdResponse) => {

      // qr code
      if (state.value === BankIdState.ExternalDevice) {
        qrCodeSrc.value = bankIdResponse?.qrCode ?? null;
      }

      if (bankIdResponse?.status === 'pending') {
        return;
      }

      if (bankIdResponse?.status === 'complete') {
        bankIdSignLoading.value = true;
        clearPolling();

        if (__props.sign) {
          emit('signed');
          return;
        }

        if (getRedirectUrl()) {
          window.location.href = getRedirectUrl() ?? '/';
        }

        return;
      }

      if (bankIdResponse?.status === 'failed' || bankIdResponse?.status === 'cancelled') {
        clearPolling();

        if (__props.sign) {
          emit('failed', bankIdResponse?.hintCode);
        }

        errorMessage.value = bankIdResponse?.hintCode ?? 'Något gick fel';
        bankIdSignLoading.value = false;
      }
    })
    .catch((e: unknown) => {
      handleError(e);
      bankIdSignLoading.value = false;
    });
  }, 5000);
};

const handleError = (e: unknown) => {
  clearPolling();
  errorMessage.value = "Något gick fel";
  console.error(e);
};

const isMobileDevice = () => {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const getRedirectUrl = (): string | null => {
  // Parameter has first dibs
  const params = new URLSearchParams(window.location.search);
    let redirectUrl = params.get('ReturnUrl');

  if (!redirectUrl) {
    // No parameter? Okay, is there data on the app div?
    const app = document.getElementById('bank-id-app');
      redirectUrl = app?.dataset.redirectUrl ?? null;
  }

  return redirectUrl;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (preamble.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(preamble.value), 1 /* TEXT */))
      : _createCommentVNode("v-if", true),
    (bankIdSignLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(Spinner),
          _createElementVNode("p", null, _toDisplayString(translations.value?.waitingForBankId), 1 /* TEXT */)
        ]))
      : _createCommentVNode("v-if", true),
    (qrCodeSrc.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("img", {
            src: qrCodeSrc.value,
            alt: "BankID QR",
            width: "50",
            height: "50"
          }, null, 8 /* PROPS */, _hoisted_5)
        ]))
      : _createCommentVNode("v-if", true),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(errorMessage.value), 1 /* TEXT */))
      : _createCommentVNode("v-if", true),
    (_unref(translationsLoading))
      ? (_openBlock(), _createBlock(Spinner, { key: 4 }))
      : (_openBlock(), _createElementBlock("div", {
          key: 5,
          class: _normalizeClass(["bank-id-app__buttons", `bank-id-app__buttons--${state.value}`])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buttons.value, (button) => {
            return (_openBlock(), _createElementBlock("button", {
              key: button.text,
              class: _normalizeClass(["button", button.modifier]),
              onClick: ($event: any) => (button.action())
            }, _toDisplayString(button.text), 11 /* TEXT, CLASS, PROPS */, _hoisted_7))
          }), 128 /* KEYED_FRAGMENT */))
        ], 2 /* CLASS */))
  ]))
}
}

})